import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const MembersSettingsPageSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e0e0e0">
      <Box>
        <Box sx={{ mb: { xs: 3, md: 5 } }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            mb: 1
          }}>
            <Skeleton height={isMobile ? 36 : 48} width={isMobile ? 200 : 250} />
            <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Skeleton height={40} width={isMobile ? '100%' : 120} style={{ borderRadius: '12px' }} />
              <Skeleton height={40} width={isMobile ? '100%' : 160} style={{ borderRadius: '12px' }} />
            </Box>
          </Box>
          <Skeleton height={isMobile ? 20 : 28} width={isMobile ? '100%' : 500} />
        </Box>

        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 4 } }}>
          {[...Array(3)].map((_, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Card sx={{
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              }}>
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', sm: 'row' },
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Skeleton circle width={isMobile ? 48 : 56} height={isMobile ? 48 : 56} />
                    <Box>
                      <Skeleton height={isMobile ? 32 : 40} width={60} style={{ marginBottom: '4px' }} />
                      <Skeleton height={isMobile ? 14 : 16} width={isMobile ? 100 : 120} />
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: '20px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Skeleton circle width={isMobile ? 40 : 48} height={isMobile ? 40 : 48} />
                  <Box>
                    <Skeleton height={isMobile ? 22 : 28} width={isMobile ? 150 : 200} />
                    <Skeleton height={isMobile ? 14 : 16} width={isMobile ? 120 : 150} />
                  </Box>
                </Box>

                {isMobile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[...Array(3)].map((_, index) => (
                      <Card key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                          <Skeleton circle width={32} height={32} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton height={16} width="80%" />
                            <Skeleton height={14} width="60%" />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Skeleton height={24} width={80} style={{ borderRadius: '8px' }} />
                          <Skeleton circle width={32} height={32} />
                        </Box>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Skeleton height={24} width={120} />
                        <Skeleton height={24} width={150} />
                        <Skeleton height={24} width={100} />
                        <Skeleton height={24} width={120} />
                        <Skeleton height={24} width={80} />
                      </Box>
                    </Box>

                    {[...Array(3)].map((_, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Skeleton circle width={32} height={32} />
                          <Skeleton height={20} width={120} />
                        </Box>
                        <Skeleton height={20} width={150} />
                        <Skeleton height={32} width={100} style={{ borderRadius: '8px' }} />
                        <Skeleton height={20} width={120} />
                        <Skeleton circle width={32} height={32} />
                      </Box>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{
              borderRadius: '20px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Skeleton circle width={isMobile ? 40 : 48} height={isMobile ? 40 : 48} />
                  <Box>
                    <Skeleton height={isMobile ? 22 : 28} width={isMobile ? 170 : 220} />
                    <Skeleton height={isMobile ? 14 : 16} width={isMobile ? 140 : 180} />
                  </Box>
                </Box>

                {isMobile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[...Array(2)].map((_, index) => (
                      <Card key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                          <Skeleton circle width={32} height={32} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton height={16} width="80%" />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                          <Skeleton height={22} width={60} style={{ borderRadius: '12px' }} />
                          <Skeleton height={22} width={60} style={{ borderRadius: '12px' }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Skeleton height={14} width={100} />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Skeleton circle width={32} height={32} />
                            <Skeleton circle width={32} height={32} />
                          </Box>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Skeleton height={24} width={150} />
                        <Skeleton height={24} width={100} />
                        <Skeleton height={24} width={100} />
                        <Skeleton height={24} width={120} />
                        <Skeleton height={24} width={120} />
                        <Skeleton height={24} width={80} />
                      </Box>
                    </Box>

                    {[...Array(2)].map((_, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Skeleton circle width={32} height={32} />
                          <Skeleton height={20} width={150} />
                        </Box>
                        <Skeleton height={24} width={80} style={{ borderRadius: '12px' }} />
                        <Skeleton height={24} width={80} style={{ borderRadius: '12px' }} />
                        <Skeleton height={20} width={120} />
                        <Skeleton height={20} width={120} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Skeleton circle width={32} height={32} />
                          <Skeleton circle width={32} height={32} />
                        </Box>
                      </Box>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </SkeletonTheme>
  );
};

export default MembersSettingsPageSkeleton;
